import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import connectoriprangesService from '../services/connectoriprangesService';
import connectorService from '../services/connectorService';
import accountService from '../services/accountService';
import './ConnectorIPRangeForm.css';

function ConnectorIPRangeForm({ mode = 'create' }) {
  const { id, connectorid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    connectorid: connectorid || '',
    accountid: '',
    accountName: '',
    allowfrom: '',
    allowto: ''
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (connectorid) {
        setFormData(prev => ({ ...prev, connectorid }));
        // Fetch connector to get accountid
        await fetchConnectorAccountId();
      }

      if (mode !== 'create' && id) {
        await fetchIPRange();
      }
    };

    initializeForm();
  }, [id, mode, connectorid]);

  const fetchConnectorAccountId = async () => {
    try {
      const connector = await connectorService.getConnectorById(connectorid);
      if (connector && connector.accountid) {
        // Fetch account name
        const account = await accountService.getAccountById(connector.accountid);
        setFormData(prev => ({
          ...prev,
          accountid: connector.accountid,
          accountName: account?.accountname || `Account ${connector.accountid}`
        }));
      }
    } catch (err) {
      console.error('Error fetching connector accountid:', err);
    }
  };

  const fetchIPRange = async () => {
    try {
      setLoading(true);
      const response = await connectoriprangesService.getConnectorIPRangeById(id);
      if (response.iprange) {
        setFormData(response.iprange);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch IP range');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.allowfrom || !formData.allowto) {
      setError('Allow From and Allow To are required');
      return;
    }

    try {
      setLoading(true);
      if (mode === 'create') {
        await connectoriprangesService.createConnectorIPRange(formData);
      } else if (mode === 'edit') {
        await connectoriprangesService.updateConnectorIPRange(id, formData);
      }
      navigate(`/connectors/${connectorid}`);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${mode} IP range`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this IP range?')) {
      return;
    }

    try {
      setLoading(true);
      await connectoriprangesService.deleteConnectorIPRange(id);
      navigate(`/connectors/${connectorid}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete IP range');
      setLoading(false);
    }
  };

  if (loading && mode !== 'create') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h2>
          {mode === 'create' && 'Add IP Range'}
          {mode === 'edit' && 'Edit IP Range'}
          {mode === 'view' && 'IP Range Details'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(`/connectors/${connectorid}`)}
          className="btn-secondary"
        >
          Back to Connector
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>IP Range Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="allowfrom">Allow From (IP Address) *</label>
              <input
                type="text"
                id="allowfrom"
                name="allowfrom"
                value={formData.allowfrom || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="e.g., 192.168.1.1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="allowto">Allow To (IP Address) *</label>
              <input
                type="text"
                id="allowto"
                name="allowto"
                value={formData.allowto || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="e.g., 192.168.1.255"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="accountName">Account</label>
              <input
                type="text"
                id="accountName"
                name="accountName"
                value={formData.accountName || ''}
                disabled={true}
              />
            </div>

            <div className="form-group">
              <label htmlFor="connectorid">Connector ID</label>
              <input
                type="number"
                id="connectorid"
                name="connectorid"
                value={formData.connectorid || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          {mode === 'view' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/connector/${connectorid}/iprange/${id}/edit`)}
                className="btn-primary"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn-danger"
                disabled={loading}
              >
                Delete
              </button>
            </>
          )}
          {(mode === 'create' || mode === 'edit') && (
            <>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/connectors/${connectorid}`)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default ConnectorIPRangeForm;
